import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type {
  CreateKitchenStationRequest,
  KitchenStationDto,
  UpdateKitchenStationRequest,
} from './types';

// Keyed per-branch so switching the branch filter doesn't blow away the
// other branch's cache. Empty/undefined branchId is intentionally not
// queried — the page disables the query until a branch is picked.
const QK_BRANCH = (branchId: string | null | undefined) =>
  ['kitchen-stations', branchId ?? 'none'] as const;

export function useKitchenStations(branchId: string | null | undefined) {
  return useQuery({
    queryKey: QK_BRANCH(branchId),
    enabled: !!branchId,
    queryFn: async () =>
      (
        await apiClient.get<KitchenStationDto[]>(
          `/api/kitchen/stations?branchId=${branchId}`,
        )
      ).data,
  });
}

export function useCreateKitchenStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateKitchenStationRequest) =>
      (
        await apiClient.post<KitchenStationDto>(
          '/api/kitchen/stations',
          request,
        )
      ).data,
    onSuccess: (created) =>
      qc.invalidateQueries({ queryKey: QK_BRANCH(created.branchId) }),
  });
}

export function useUpdateKitchenStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      request,
    }: {
      id: string;
      request: UpdateKitchenStationRequest;
    }) =>
      (
        await apiClient.put<KitchenStationDto>(
          `/api/kitchen/stations/${id}`,
          request,
        )
      ).data,
    onSuccess: (updated) =>
      qc.invalidateQueries({ queryKey: QK_BRANCH(updated.branchId) }),
  });
}

export function useDeleteKitchenStation(branchId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/kitchen/stations/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_BRANCH(branchId) }),
  });
}
